import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import {
  collection,
  documentId,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { CurrencyType, db } from '../../config';
import { AttractionType, StatusType, TranslationStatus, TranslationType } from '../../types';
import { convertFirestoreTimestampToString } from '../utils';

export interface ResponseAttractionDataProps {
    id: string;
    title: TranslationType;
    address: string;
    city: string;
    createdAt: string;
    thumbnail: string;
    translationStatus: TranslationStatus;
    type: AttractionType;
    createdBy: string;
    status: StatusType;
    price: number;
    currency: CurrencyType;
    email: string;
    duration: number;
    languages: string[];
    endDate?: string;
}

const chunkArray = <T>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

export const useGetAttractionCollectionFirebase = (
  options?: UseQueryOptions<ResponseAttractionDataProps[], FirebaseError>,
): UseQueryResult<ResponseAttractionDataProps[], FirebaseError> => useQuery<
ResponseAttractionDataProps[],
FirebaseError
>({
  queryKey: ['attractions'],
  queryFn: async (): Promise<ResponseAttractionDataProps[]> => {
    const attractionsSnap = await getDocs(
      query(collection(db, 'attractions'), orderBy('createdAt', 'desc')),
    );

    const creatorIds = new Set<string>();
    attractionsSnap.docs.forEach((doc) => {
      const createdBy = doc.data()?.createdBy;
      if (typeof createdBy === 'string' && createdBy) creatorIds.add(createdBy);
    });

    const creatorMap = new Map<string, string>();
    if (creatorIds.size > 0) {
      const chunks = chunkArray([...creatorIds], 10);
      await Promise.all(
        chunks.map(async (chunk) => {
          const usersSnap = await getDocs(
            query(collection(db, 'users'), where(documentId(), 'in', chunk)),
          );
          usersSnap.docs.forEach((userDoc) => {
            const u = userDoc.data();
            const fullName = `${u?.name ?? ''} ${u?.surname ?? ''}`.trim();
            creatorMap.set(userDoc.id, fullName);
          });
        }),
      );
    }

    return attractionsSnap.docs.map((doc) => {
      const item = doc.data();
      const creatorId = typeof item?.createdBy === 'string' ? item.createdBy : '';
      return {
        id: doc.id,
        title: item.title || {},
        address: item.address || '',
        city: item.city || '',
        createdAt: convertFirestoreTimestampToString(item.createdAt),
        thumbnail: item.thumbnail || '',
        translationStatus: item.translationStatus || '',
        type: item.type || '',
        createdBy: creatorMap.get(creatorId) || '',
        status: item?.status ?? null,
        price: item?.price || 0,
        currency: item?.currency || 'PLN',
        email: item?.email || '',
        duration: item?.duration || 1,
        endDate: item?.endDate || '',
        languages: item.languages || [],
      } as ResponseAttractionDataProps;
    });
  },
  retry: 1,
  staleTime: 1000 * 60 * 5,
  ...options,
});
